import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import {
    IconButton,
    MenuItem,
    FormLabel,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Box,
    Grid,
    CircularProgress,
    Typography,
    InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import AppContext from '../../../App/AppContext';
import { getStandardWriteUp, setNativeLanguage } from '../../../Core/Service/techWriteUp-service';
import { AppStorage } from '../../../Core/Service/storage-service';
import { DialogContents, DialogTitles, Dialogs, SecondaryButton, TextBox } from '../../../Core/FormInput';

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        height: '85vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
            height: '92vh',
            maxHeight: '96vh'
        },
        [theme.breakpoints.down('xs')]: {
            height: '96vh',
            maxHeight: '98vh',
            margin: theme.spacing(1),
            width: 'calc(100% - 16px)'
        }
    },
    dialogHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 24px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        flexShrink: 0,
        [theme.breakpoints.down('xs')]: {
            padding: '10px 14px'
        }
    },
    dialogTitle: {
        padding: 0,
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1a202c'
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        padding: '14px 24px 20px',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
            padding: '10px 12px 14px'
        }
    },
    filterControls: {
        flexShrink: 0,
        marginBottom: 10
    },
    tableContainer: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 4,
        backgroundColor: '#fff',
        '&::-webkit-scrollbar': {
            width: 8,
            height: 8
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4
        }
    },
    table: {
        width: '100%',
        borderCollapse: 'separate'
    },
    tableHeaderCell: {
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 14px',
        backgroundColor: '#f5f5f5',
        color: '#333',
        borderBottom: '2px solid #e0e0e0'
    },
    tableBody: {
        padding: '0px !important'
    },
    tableRow: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.12) !important'
        }
    },
    selectedRow: {
        backgroundColor: 'rgba(255, 152, 0, 0.28) !important',
        borderLeft: '4px solid #f57c00'
    },
    tableCell: {
        padding: '10px 14px',
        fontSize: 13,
        lineHeight: 1.5,
        borderBottom: '1px solid #eeeeee'
    },
    labelRed: {
        color: '#d32f2f',
        fontWeight: 600,
        textTransform: 'uppercase'
    },
    centerBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 200,
        gap: 12
    },
    selectButton: {
        borderRadius: 20,
        minWidth: 90,
        height: 38,
        fontWeight: 600
    }
}));

const DisplayGrid = (props) => {
    const classes = useStyles();
    const { hideModal, showToast, getUserPreference } = useContext(AppContext);

    const [rows, setRows] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [enLanguages, setEnLanguages] = useState([]);
    const [langCode, setLangCode] = useState(() => AppStorage.getUserNativeLanguageCode() || 'en');
    const [filterText, setFilterText] = useState('');
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        try {
            const nativeLang = AppStorage.getUserNativeLanguageCode();
            if (nativeLang) {
                setLangCode(nativeLang);
            } else {
                const userPref = getUserPreference();
                if (userPref?.details?.[0]?.languageCode) {
                    setLangCode(userPref.details[0].languageCode);
                }
            }
        } catch (e) {
            console.error('Error fetching user native language:', e);
        }
    }, []);

    const pullStandardAndUpdateState = useCallback(
        async (currentLangCode) => {
            try {
                setLoading(true);
                const result = await getStandardWriteUp('N', currentLangCode);
                if (result?.success) {
                    const list = result.data?.list || [];
                    setRows(list);
                    setLanguages(result.data?.languages || []);
                    if (currentLangCode === 'en') {
                        setEnLanguages(list);
                    }
                } else {
                    showToast(result?.message || 'Failed to fetch standard write-ups');
                }
            } catch (err) {
                console.error('Error fetching standard write-ups:', err);
                showToast('Something went wrong loading standard write-ups');
            } finally {
                setLoading(false);
            }
        },
        [showToast]
    );

    useEffect(() => {
        pullStandardAndUpdateState(langCode);
    }, [langCode, pullStandardAndUpdateState]);

    const handleClose = () => {
        if (props.onFormSubmit) props.onFormSubmit();
        if (hideModal) hideModal();
    };

    const onSubmitStandard = (rowData) => {
        const targetRow = rowData || selectedRowData;
        if (!targetRow) return;

        if (langCode === 'en') {
            props.onStandardWriteup(targetRow);
        } else {
            const findEnglish = enLanguages.find((a) => a.id === targetRow.id);
            props.onStandardWriteup(findEnglish || targetRow);
        }
        if (props.onFormSubmit) {
            props.onFormSubmit();
        }
    };

    const filteredRows = useMemo(() => {
        const trimmed = filterText.trim().toLowerCase();
        if (!trimmed) return rows;

        const tokens = trimmed.split(/\s+/).filter(Boolean);
        return rows.filter((row) => {
            const textToSearch = `${row.complaint || ''} ${row.cause || ''} ${row.cure || ''}`.toLowerCase();
            return tokens.every((token) => textToSearch.includes(token));
        });
    }, [filterText, rows]);

    const handleRowClick = (row) => (event) => {
        if (event.detail === 2) {
            onSubmitStandard(row);
        } else {
            setSelectedRowData((prev) => (prev?.id === row.id ? null : row));
        }
    };

    const currentLanguageObj = useMemo(() => {
        return languages?.find((l) => l.id === langCode || l.languageCode === langCode);
    }, [languages, langCode]);

    const complaintLabel = currentLanguageObj?.languageComplaint || currentLanguageObj?.LanguageComplaint || currentLanguageObj?.complaint || 'Complaint';
    const causeLabel = currentLanguageObj?.languageCause || currentLanguageObj?.LanguageCause || currentLanguageObj?.cause || 'Cause';
    const cureLabel = currentLanguageObj?.languageCure || currentLanguageObj?.LanguageCure || currentLanguageObj?.cure || 'Cure';

    const handleLanguageChange = async (e) => {
        const selectedLang = e.target.value;
        setLangCode(selectedLang);
        setSelectedRowData(null);
        AppStorage.setUserNativeLanguageCode(selectedLang);
        try {
            await setNativeLanguage(selectedLang);
        } catch (error) {
            console.error('Failed to set native language:', error);
        }
    };

    const clearSearch = () => {
        setFilterText('');
    };

    return (
        <Dialogs open={true} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ className: classes.dialogPaper }}>
            <Box className={classes.dialogHeader}>
                <DialogTitles className={classes.dialogTitle}>Select Standard Write Up</DialogTitles>
                <IconButton onClick={handleClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContents className={classes.dialogContent}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between" className={classes.filterControls}>
                    <Grid item xs={12} sm={8}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={8} md={8}>
                                <TextBox
                                    fullWidth
                                    size="small"
                                    margin="dense"
                                    name="Search"
                                    label="Search"
                                    required
                                    autoFocus
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: filterText ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={clearSearch} tabIndex={-1}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={4}>
                                <SecondaryButton className={classes.selectButton} onClick={() => onSubmitStandard()} disabled={!selectedRowData}>
                                    Select
                                </SecondaryButton>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormLabel component="legend" style={{ fontSize: 12, marginBottom: 2 }}>
                            Language
                        </FormLabel>
                        <TextBox select size="small" margin="dense" fullWidth value={langCode} name="langCode" onChange={handleLanguageChange}>
                            {languages?.length > 0 ? (
                                languages.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value={langCode}>{langCode === 'en' ? 'English' : langCode}</MenuItem>
                            )}
                        </TextBox>
                    </Grid>
                </Grid>

                <TableContainer className={classes.tableContainer}>
                    {loading ? (
                        <Box className={classes.centerBox}>
                            <CircularProgress size={36} color="secondary" />
                            <Typography variant="body2" color="textSecondary">
                                Loading standard write-ups...
                            </Typography>
                        </Box>
                    ) : filteredRows.length === 0 ? (
                        <Box className={classes.centerBox}>
                            <Typography variant="body1" color="textSecondary">
                                {filterText ? `No standard write-up found matching "${filterText}"` : 'No standard write-ups available'}
                            </Typography>
                        </Box>
                    ) : (
                        <Table className={classes.table} stickyHeader aria-label="Standard Write Up Table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableHeaderCell} align="left">
                                        Standard Write Up ({filteredRows.length})
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody className={classes.tableBody}>
                                {filteredRows.map((row) => {
                                    const isSelected = selectedRowData?.id === row.id;
                                    return (
                                        <TableRow
                                            hover
                                            role="button"
                                            tabIndex={0}
                                            key={row.id}
                                            className={`${classes.tableRow} ${isSelected ? classes.selectedRow : ''}`}
                                            onClick={handleRowClick(row)}
                                            onDoubleClick={() => onSubmitStandard(row)}
                                        >
                                            <TableCell className={classes.tableCell} component="td" scope="row">
                                                <Typography variant="body2" component="div">
                                                    <span className={classes.labelRed}>{complaintLabel}:</span> {row.complaint || '-'},{' '}
                                                    <span className={classes.labelRed}>{causeLabel}:</span> {row.cause || '-'},{' '}
                                                    <span className={classes.labelRed}>{cureLabel}:</span> {row.cure || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </DialogContents>
        </Dialogs>
    );
};

export default DisplayGrid;
